import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import { toastify } from "../../../../../../utils/toast";
import { useFacilityStore } from "../../../../../../app/z-store/store";
import { updateSpinner } from "../../../../../../features/authentication/authenticationReducer";
import { availableWaterSensorGatewaysURL, waterSensorGatewaysURL } from "../../../../../../utils/urls";
import { formatDateTime, timeAgo } from "../utils/format";

const REFRESH_MS = 60000;

// 4G gateways (USR-DR512, identified by IMEI) assigned to the selected facility.
// A facility only sees tanks on its own gateways.
function GatewaysTab() {
  const dispatch = useDispatch();
  const facilityId = useFacilityStore((state) => state.facilityId);
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [available, setAvailable] = useState([]);
  const [form, setForm] = useState({ gatewayId: "", name: "" });
  const [editing, setEditing] = useState(null); // gateway being renamed
  const [removing, setRemoving] = useState(null); // gateway pending removal

  const getGateways = async () => {
    if (!facilityId) return;
    const response = await makeRequest2(waterSensorGatewaysURL(facilityId), "GET");
    if (response.success) setGateways(response.data.data || []);
    else toastify(response.error, "error");
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    setGateways([]);
    getGateways();
    const refresh = setInterval(getGateways, REFRESH_MS);
    return () => clearInterval(refresh);
  }, [facilityId]);

  const openAssign = async () => {
    setEditing(null);
    setForm({ gatewayId: "", name: "" });
    setShowAssign(true);
    const response = await makeRequest2(availableWaterSensorGatewaysURL(facilityId), "GET");
    if (response.success) {
      const list = response.data.data || [];
      setAvailable(list);
      if (list.length) setForm((f) => ({ ...f, gatewayId: f.gatewayId || list[0].gatewayId }));
    } else {
      toastify(response.error, "error");
    }
  };

  const openRename = (gateway) => {
    setEditing(gateway);
    setForm({ gatewayId: gateway.gatewayId, name: gateway.name || "" });
    setShowAssign(true);
  };

  const handleSave = async () => {
    if (!form.gatewayId.trim()) {
      toastify("Choose or enter the gateway IMEI", "error");
      return;
    }
    dispatch(updateSpinner(true));
    const response = await makeRequest2(waterSensorGatewaysURL(facilityId), "POST", {
      gatewayId: form.gatewayId.trim(),
      name: form.name.trim(),
    });
    dispatch(updateSpinner(false));
    if (response.success) {
      toastify(editing ? "Gateway updated" : "Gateway assigned to this facility", "success");
      setShowAssign(false);
      getGateways();
    } else {
      toastify(response.error, "error");
    }
  };

  const handleRemove = async () => {
    dispatch(updateSpinner(true));
    const response = await makeRequest2(`${waterSensorGatewaysURL(facilityId)}/${encodeURIComponent(removing.gatewayId)}`, "DELETE");
    dispatch(updateSpinner(false));
    if (response.success) {
      toastify("Gateway removed from this facility", "success");
      setRemoving(null);
      getGateways();
    } else {
      toastify(response.error, "error");
    }
  };

  const nameTemplate = (g) => (
    <div>
      <div className="fw-semibold">{g.name || "Unnamed gateway"}</div>
      <div className="text-muted small">IMEI {g.gatewayId}</div>
    </div>
  );

  const statusTemplate = (g) =>
    g.connected ? (
      <span className="badge status-badge bg-light-success text-success">
        <i className="ti ti-plug-connected me-1"></i>Connected
      </span>
    ) : (
      <span className="badge status-badge bg-light-secondary text-secondary">
        <i className="ti ti-plug-connected-x me-1"></i>Not connected
      </span>
    );

  const actionTemplate = (g) => (
    <div className="d-flex gap-2">
      <button className="btn btn-sm btn-outline-primary" onClick={() => openRename(g)}>
        <i className="ti ti-pencil me-1"></i> Rename
      </button>
      <button className="btn btn-sm btn-outline-danger" onClick={() => setRemoving(g)}>
        <i className="ti ti-unlink me-1"></i> Remove
      </button>
    </div>
  );

  const pickedAvailable = available.find((a) => a.gatewayId === form.gatewayId);

  return (
    <div className="card">
      <div className="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">Gateways</h5>
          <div className="text-muted small">4G gateways (USR-DR512) at this facility. Only their tanks show on the other tabs.</div>
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={openAssign}>
          <i className="ti ti-plus me-1"></i> Assign gateway
        </button>
      </div>
      <div className="card-body">
        <DataTable
          value={gateways}
          loading={loading}
          emptyMessage="No gateway assigned to this facility yet."
          stripedRows
          responsiveLayout="scroll"
        >
          <Column header="Gateway" body={nameTemplate} />
          <Column header="Status" body={statusTemplate} />
          <Column field="tankCount" header="Tanks" />
          <Column header="Last reading" body={(g) => <span title={formatDateTime(g.lastSeen)}>{timeAgo(g.lastSeen)}</span>} />
          <Column header="Assigned" body={(g) => formatDateTime(g.assignedAt)} />
          <Column header="" body={actionTemplate} />
        </DataTable>
      </div>

      {/* Assign / rename */}
      <Dialog
        header={editing ? "Rename gateway" : "Assign gateway to this facility"}
        visible={showAssign}
        style={{ width: "min(520px, 95vw)" }}
        onHide={() => setShowAssign(false)}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {editing ? (
            <div className="mb-3">
              <label className="form-label">Gateway IMEI</label>
              <input className="form-control" value={form.gatewayId} disabled />
            </div>
          ) : (
            <>
              {available.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Unassigned gateways</label>
                  <select
                    className="form-select"
                    value={available.some((a) => a.gatewayId === form.gatewayId) ? form.gatewayId : ""}
                    onChange={(e) => setForm({ ...form, gatewayId: e.target.value })}
                  >
                    <option value="">Enter IMEI manually…</option>
                    {available.map((a) => (
                      <option key={a.gatewayId} value={a.gatewayId}>
                        {a.gatewayId} · {a.connected ? "connected" : "offline"} · {a.tankCount} tank{a.tankCount === 1 ? "" : "s"}
                      </option>
                    ))}
                  </select>
                  {pickedAvailable && (
                    <div className="form-text">Last reading {timeAgo(pickedAvailable.lastSeen)}</div>
                  )}
                </div>
              )}
              {!pickedAvailable && (
                <div className="mb-3">
                  <label className="form-label">Gateway IMEI</label>
                  <input
                    className="form-control"
                    placeholder="e.g. 862790078641100"
                    value={form.gatewayId}
                    onChange={(e) => setForm({ ...form, gatewayId: e.target.value })}
                  />
                  <div className="form-text">
                    The DR512's IMEI (its registration ID, shown by AT+IMEI?). You can add it before the gateway is installed.
                  </div>
                </div>
              )}
            </>
          )}
          <div className="mb-3">
            <label className="form-label">Name</label>
            <input
              className="form-control"
              placeholder="e.g. Block A rooftop"
              maxLength={100}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setShowAssign(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editing ? "Save" : "Assign"}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Remove confirmation */}
      <Dialog header="Remove gateway" visible={!!removing} style={{ width: "min(460px, 95vw)" }} onHide={() => setRemoving(null)}>
        {removing && (
          <>
            <p>
              Remove <b>{removing.name || removing.gatewayId}</b> from this facility? Its {removing.tankCount} tank
              {removing.tankCount === 1 ? "" : "s"} will no longer show here. Readings are kept and come back if you assign it again.
            </p>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-outline-secondary" onClick={() => setRemoving(null)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={handleRemove}>
                Remove
              </button>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}

export default GatewaysTab;
